import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const BookInspectionModal = ({ listing, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    try {
      const inspectionMessage = `Inspection Request:\nPreferred Date: ${formData?.preferredDate}\nPreferred Time: ${formData?.preferredTime}\n${formData?.message ? `Additional Notes: ${formData?.message}` : ''}`;

      const { error: leadError } = await supabase?.from('leads')?.insert([
          {
            listing_id: listing?.id,
            type: 'inspection',
            name: formData?.name,
            email: formData?.email,
            phone: formData?.phone,
            message: inspectionMessage,
            stage: 'new'
          }
        ]);

      if (leadError) throw leadError;

      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', preferredDate: '', preferredTime: '', message: '' });
    } catch (error) {
      console.error('Error booking inspection:', error);
      setError('Failed to book inspection. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground">Book an Inspection</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Close"
          >
            <Icon name="X" size={20} color="currentColor" />
          </button>
        </div>

        <div className="p-6">
          {success ? (
            <div className="space-y-4 text-center">
              <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto">
                <Icon name="CheckCircle2" size={32} color="var(--color-success)" />
              </div>
              <h3 className="text-xl font-bold text-foreground">Inspection Booked!</h3>
              <p className="text-muted-foreground">
                Thank you for your interest. An agent will contact you shortly to confirm your inspection time.
              </p>
              <Button variant="default" fullWidth onClick={onClose}>
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-start gap-2">
                  <Icon name="AlertCircle" size={16} color="var(--color-error)" className="mt-0.5" />
                  <p className="text-sm text-error">{error}</p>
                </div>
              )}

              <Input
                label="Your Name"
                type="text"
                value={formData?.name}
                onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                required
                disabled={loading}
              />

              <Input
                label="Email Address"
                type="email"
                value={formData?.email}
                onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                required
                disabled={loading}
              />

              <Input
                label="Phone Number"
                type="tel"
                value={formData?.phone}
                onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })}
                required
                disabled={loading}
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Preferred Date"
                  type="date"
                  value={formData?.preferredDate}
                  onChange={(e) => setFormData({ ...formData, preferredDate: e?.target?.value })}
                  required
                  disabled={loading}
                  min={new Date()?.toISOString()?.split('T')?.[0]}
                />

                <Input
                  label="Preferred Time"
                  type="time"
                  value={formData?.preferredTime}
                  onChange={(e) => setFormData({ ...formData, preferredTime: e?.target?.value })}
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Additional Notes (Optional)
                </label>
                <textarea
                  value={formData?.message}
                  onChange={(e) => setFormData({ ...formData, message: e?.target?.value })}
                  disabled={loading}
                  rows={3}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Any specific requirements or questions?"
                />
              </div>

              <div className="flex gap-3">
                <Button type="button" variant="outline" fullWidth onClick={onClose} disabled={loading}>
                  Cancel
                </Button>
                <Button type="submit" variant="default" fullWidth loading={loading}>
                  Book Inspection
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookInspectionModal;